import React, { useEffect, useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { Display, TableProps, Table } from "@apptronik/react-exoskeleton";
import { LicenseDialog, License, LicenseFields } from "./LicenseDialog";
import query_data from "../../../js/query_data";

type Columns = {
    spdx_id: string;
    full_name: string;
    is_approved: React.ReactNode;
    edit?: React.ReactNode;
};

export default function LicensePage() {
    const [licenses, setLicenses] = useState<License[]>([]);
    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
    const [self, setSelf] = useState(null);
    const [loading, setLoading] = useState(false);

    const isAdmin = (): boolean => {
        if (!self || !self.roles) return false;
        return self.roles.some(role => role.name === 'universal_admin' || role.name === 'ApptronikAdmin');
    };

    const fetchLicenses = async () => {
        try {
            const data = await query_data("/licenses", {});
            setLicenses(data);
        } catch (error) {
            console.error("Failed to fetch licenses:", error);
        }
    };

    const fetchSelf = async () => {
        try {
            const data = await query_data('/users/get_self', {});
            setSelf(data[0]);
        } catch (error) {
            console.error("Failed to fetch user data:", error);
        }
    };

    useEffect(() => {
        fetchLicenses();
    }, [openAdd, openEdit]);

    useEffect(() => {
        fetchSelf();
    }, []);

    const handleOpenAdd = () => setOpenAdd(true);
    const handleCloseAdd = () => setOpenAdd(false);
    const handleOpenEdit = (license: License) => {
        setSelectedLicense(license);
        setOpenEdit(true);
    };
    const handleCloseEdit = () => {
        setSelectedLicense(null);
        setOpenEdit(false);
    };

    const handleSubmitAdd = async (license: LicenseFields) => {
        setLoading(true);
        try {
            await query_data("/licenses", license, "POST");
            setOpenAdd(false);
            fetchLicenses();
        } catch (error) {
            console.error("Failed to create license:", error);
            alert("Failed to create license. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitEdit = async (license: License) => {
        setLoading(true);
        try {
            await query_data(`/licenses/${license.id}`, license, "PUT");
            setOpenEdit(false);
            fetchLicenses();
        } catch (error) {
            console.error("Failed to update license:", error);
            alert("Failed to update license. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const rows = licenses.map((lic) => {
        const rowsConfig = {
            link: "",
            cells: {
                spdx_id: lic.spdx_id,
                full_name: lic.full_name ?? "",
                is_approved: lic.is_approved ? (
                    <Chip label="Approved" color="success" size="small" />
                ) : (
                    <Chip label="Not Approved" color="default" size="small" />
                ),
            },
        };

        if (isAdmin()) {
            rowsConfig.cells["edit"] = (
                <Box sx={{ minWidth: "80px" }}>
                    <Button size="small" onClick={() => handleOpenEdit(lic)} disabled={loading}>
                        Edit
                    </Button>
                </Box>
            );
        }

        return rowsConfig;
    });

    const TableInput: TableProps<Columns> = {
        rows,
    };

    return (
        <Box sx={{ padding: "2.5rem" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Display variant="medium">Licenses</Display>
                {isAdmin() && (
                    <Button variant="contained" onClick={handleOpenAdd} disabled={loading}>
                        Add License
                    </Button>
                )}
            </Box>

            <LicenseDialog
                open={openAdd}
                mode="add"
                onSubmit={handleSubmitAdd}
                onClose={handleCloseAdd}
            />
            <LicenseDialog
                open={openEdit}
                mode="edit"
                license={selectedLicense}
                onSubmit={handleSubmitEdit}
                onClose={handleCloseEdit}
            />

            {licenses.length > 0 ? (
                <Table {...TableInput} />
            ) : (
                <p>No licenses found.</p>
            )}
        </Box>
    );
}